import React, { useState, useEffect } from "react";
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Badge,
  Modal,
  TextInput,
  Skeleton,
  ActionIcon,
  Tooltip,
  Loader,
  Alert,
} from "@mantine/core";
import {
  IconPhone,
  IconPlus,
  IconTrash,
  IconSearch,
  IconInfoCircle,
} from "@tabler/icons-react";
import { notifyError, notifySuccess } from "../../utils/notify";
import { useVoiceAI } from "../../context/VoiceAIContext";
import { formatPhoneNumberDisplay as formatPhoneNumber } from "../../utils/phoneUtils";

export default function PhoneNumbersTab() {
  const {
    phoneNumbers,
    phoneNumbersLoading,
    addPhoneNumber,
    deletePhoneNumber,
    availableNumbers,
    availableNumbersLoading,
    searchAvailableNumbers,
  } = useVoiceAI();

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [areaCode, setAreaCode] = useState("");
  const [selectedNumber, setSelectedNumber] = useState("");
  const [addingNumber, setAddingNumber] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    id: number;
    phone_number: string;
  } | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Auto-search when modal opens (no area code = broad search)
  useEffect(() => {
    if (addModalOpen && !hasSearched) {
      searchAvailableNumbers(undefined);
      setHasSearched(true);
    }
  }, [addModalOpen, hasSearched, searchAvailableNumbers]);

  const handleSearch = () => {
    searchAvailableNumbers(areaCode || undefined);
  };

  const handleAddDirect = async (number: string) => {
    setSelectedNumber(number);
    setAddingNumber(true);
    try {
      await addPhoneNumber(number, "Main Line");
      notifySuccess(`${formatPhoneNumber(number)} is now active`, {
        title: "Phone number added",
      });
      setAddModalOpen(false);
      setSelectedNumber("");
      setAreaCode("");
      setHasSearched(false);
    } catch (error: any) {
      notifyError(error.message || "Please try again", {
        title: "Failed to add number",
      });
    } finally {
      setAddingNumber(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await deletePhoneNumber(id);
      notifySuccess("The number has been disconnected from Voice AI", {
        title: "Phone number removed",
      });
    } catch (error: any) {
      notifyError(error.message || "Please try again", {
        title: "Failed to remove number",
      });
    } finally {
      setDeletingId(null);
    }
  };

  if (phoneNumbersLoading) {
    return (
      <Stack gap="sm">
        <Skeleton height={80} />
        <Skeleton height={80} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {phoneNumbers.length}/2 phone numbers configured
        </Text>
        <Button
          leftSection={<IconPlus size={16} />}
          size="sm"
          onClick={() => setAddModalOpen(true)}
          disabled={phoneNumbers.length >= 2}
        >
          Add Phone Number
        </Button>
      </Group>

      {phoneNumbers.length === 0 ? (
        <Paper p="xl" withBorder ta="center">
          <IconPhone size={40} color="gray" />
          <Text mt="md" c="dimmed">
            No phone numbers configured. Add a phone number to start receiving
            AI-powered calls.
          </Text>
        </Paper>
      ) : (
        <Stack gap="sm">
          {phoneNumbers.map((phone) => (
            <Paper key={phone.id} p="md" withBorder>
              <Group justify="space-between">
                <Group>
                  <IconPhone size={20} />
                  <div>
                    <Text fw={500}>
                      {formatPhoneNumber(phone.phone_number)}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {phone.label}
                    </Text>
                  </div>
                </Group>
                <Group>
                  <Badge
                    color={phone.status === "active" ? "green" : "gray"}
                    variant="light"
                  >
                    {phone.status}
                  </Badge>
                  <Tooltip label="Remove number">
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      loading={deletingId === phone.id}
                      onClick={() =>
                        setConfirmDelete({
                          id: phone.id,
                          phone_number: phone.phone_number,
                        })
                      }
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Add Phone Number Modal */}
      <Modal
        opened={addModalOpen}
        onClose={() => {
          setAddModalOpen(false);
          setSelectedNumber("");
          setHasSearched(false);
        }}
        title="Add Phone Number"
        size="lg"
      >
        <Stack gap="md">
          <Alert
            icon={<IconInfoCircle size={16} />}
            color="blue"
            variant="light"
          >
            Pick a phone number for your AI receptionist. Callers to this number
            will be answered by your Voice AI assistant.
          </Alert>

          <Group align="flex-end">
            <TextInput
              label="Filter by area code (optional)"
              placeholder="e.g., 510, 628, 650"
              value={areaCode}
              onChange={(e) => setAreaCode(e.currentTarget.value)}
              maxLength={3}
              style={{ flex: 1 }}
            />
            <Button
              leftSection={<IconSearch size={16} />}
              onClick={handleSearch}
              loading={availableNumbersLoading}
            >
              Search
            </Button>
          </Group>

          {availableNumbersLoading ? (
            <Group justify="center" py="xl">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Finding available numbers...
              </Text>
            </Group>
          ) : availableNumbers.length > 0 ? (
            <Stack gap="xs">
              {availableNumbers.map((num) => (
                <Button
                  key={num.phone_number}
                  variant={
                    selectedNumber === num.phone_number ? "filled" : "outline"
                  }
                  color="teal"
                  fullWidth
                  justify="space-between"
                  rightSection={
                    <Text
                      size="xs"
                      c={
                        selectedNumber === num.phone_number ? "white" : "dimmed"
                      }
                    >
                      {num.locality}
                      {num.locality && num.region ? ", " : ""}
                      {num.region}
                    </Text>
                  }
                  loading={addingNumber && selectedNumber === num.phone_number}
                  disabled={addingNumber && selectedNumber !== num.phone_number}
                  onClick={() => handleAddDirect(num.phone_number)}
                  styles={{ root: { height: 50 } }}
                >
                  {num.friendly_name}
                </Button>
              ))}
            </Stack>
          ) : hasSearched ? (
            <Paper p="md" withBorder ta="center">
              <Text c="dimmed">
                No numbers available for this area code. Try 510, 628, 650, or
                leave blank for all areas.
              </Text>
            </Paper>
          ) : null}
        </Stack>
      </Modal>

      <Modal
        opened={confirmDelete !== null}
        onClose={() => (deletingId ? null : setConfirmDelete(null))}
        title="Remove this phone number?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            <strong>
              {confirmDelete && formatPhoneNumber(confirmDelete.phone_number)}
            </strong>{" "}
            will be released from Twilio and disconnected from Voice AI. Twilio
            phone numbers are paid resources — once released, recovering the
            same number is not guaranteed.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setConfirmDelete(null)}
              disabled={deletingId !== null}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={deletingId !== null}
              onClick={async () => {
                if (!confirmDelete) return;
                await handleDelete(confirmDelete.id);
                setConfirmDelete(null);
              }}
            >
              Remove number
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
